import { Avatar, Button, Dropdown, Menu, Typography } from "antd";
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { Outlet, useLocation, useNavigate } from "react-router";
import { anchorMenuItems } from "@/config/menu";
import { ROUTES } from "@/config/routes";
import {
  CALL_STATUS_LABEL,
  CONNECTION_STATUS_LABEL,
  VISIBILITY_STATUS_LABEL,
} from "@/constants/anchorStatus";
import { useAuthStore } from "@/store/authStore";
import { usePresenceStore } from "@/store/presenceStore";
import { StatusBadge } from "./StatusBadge";
import type { MenuProps } from "antd";

const { Text } = Typography;

export default function AnchorLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isProfilePage = location.pathname === ROUTES.profile;
  const isProfileTaskPage =
    location.pathname === ROUTES.profileInstagram ||
    location.pathname === ROUTES.profilePrivatePhotos ||
    location.pathname === ROUTES.profilePrivateVideos;
  const isWorkspacePage = location.pathname === ROUTES.workspace;
  const isWithdrawPage = location.pathname === ROUTES.withdraw;
  const isProfileCompletionPage =
    location.pathname === ROUTES.profileCompletion;
  const isImmersivePage =
    isProfilePage ||
    isProfileTaskPage ||
    isWorkspacePage ||
    isWithdrawPage ||
    isProfileCompletionPage;
  const anchor = useAuthStore((state) => state.anchor);
  const logout = useAuthStore((state) => state.logout);
  const visibilityStatus = usePresenceStore((state) => state.visibility_status);
  const callStatus = usePresenceStore((state) => state.call_status);
  const connectionStatus = usePresenceStore((state) => state.connection_status);

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => navigate(ROUTES.profile),
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log out",
      danger: true,
      onClick: async () => {
        await logout();
        navigate(ROUTES.login);
      },
    },
  ];

  return (
    <div
      className={
        isImmersivePage ? "anchor-app-layout is-immersive" : "anchor-app-layout"
      }
    >
      {!isImmersivePage && (
        <>
          <header className="anchor-app-header">
            <div className="anchor-app-statuses">
              <StatusBadge
                label={CONNECTION_STATUS_LABEL[connectionStatus]}
                status="processing"
              />
              <StatusBadge
                label={VISIBILITY_STATUS_LABEL[visibilityStatus]}
                status="default"
              />
              <StatusBadge
                label={CALL_STATUS_LABEL[callStatus]}
                status="success"
              />
            </div>

            <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
              <Button className="anchor-app-user" type="text">
                <Avatar
                  size={28}
                  src={anchor?.avatar}
                  icon={<UserOutlined />}
                />
                <Text strong ellipsis>
                  {anchor?.nickname || "Anchor"}
                </Text>
              </Button>
            </Dropdown>
          </header>

          <nav className="anchor-app-nav">
            <Menu
              mode="horizontal"
              selectedKeys={[location.pathname]}
              items={anchorMenuItems}
              onClick={({ key }) => navigate(key)}
            />
          </nav>
        </>
      )}

      <main className="anchor-app-content">
        <Outlet />
      </main>
    </div>
  );
}
